<template>
  <UserProfile>
    <InfoList
      :title="$t('pages.user_profile.profile.personal_info')"
      :data="data.profile"
      button-data-testid="open-form--edit-personal-info"
      :on-click="isApplicationEditable && openEditModal"/>
    <PersonalDocumentList/>
    <ReviewedDocumentsList
      v-if="store.reviewedDocuments?.length"
      :documents="store.reviewedDocuments"
      :download-file="OnboardingAdminRequests.Personal.getReviewedDocument"/>
    <OnboardingCommentList :application-id="applicationId"/>

    <PersonalDetailsForm
      v-if="isModalOpen.edit"
      v-model:is-modal-open="isModalOpen.edit"/>
  </UserProfile>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import InfoList from '@/components/InfoList.vue';
import { i18n as i18nGlobal } from '@/i18n';
import {
  getAccountTypeString, getDate, getPersonalCountryName, transformDashboardAddress,
} from '@/modules/onboarding/utils';
import OnboardingCommentList from '@/modules/onboarding-dashboard/shared/components/OnboardingCommentList.vue';
import { useCustomerProfileInject } from '@/modules/onboarding-dashboard/shared/utils';
import PersonalDetailsForm from '@/modules/onboarding-personal-profile/components/personal-details-form.vue';
import PersonalDocumentList from '@/modules/onboarding-personal-profile/components/personal-document-list.vue';
import ReviewedDocumentsList from '@/modules/onboarding-user-profile/components/ReviewedDocumentsList.vue';
import UserProfile from '@/modules/onboarding-user-profile/components/user-profile.vue';
import { OnboardingAdminRequests } from '@/services/requests';
import { usePersonalOnboardingStore } from '@/stores/OnboardingPersonal';
import { getRouteParam } from '@/utils/getRouteParam';

const i18n = useI18n();
const route = useRoute();

const store = usePersonalOnboardingStore();
const { isApplicationEditable } = useCustomerProfileInject();

const applicationId = getRouteParam(route.params.id);

const isModalOpen = reactive({
  edit: false,
});

function openEditModal() {
  isModalOpen.edit = true;
}

const data = computed(() => ({
  profile: [
    {
      label: i18n.t('form.label.account_type'),
      value: i18n.t(getAccountTypeString(store.applicationType, store.partnerAttributes)),
    },
    {
      label: i18n.t('form.label.nationality'),
      value: getPersonalCountryName(store.personalDetails?.nationalityIso3),
    },
    {
      label: i18n.t('form.label.date_of_birth'),
      value: getDate(store.personalDetails?.dateOfBirth),
    },
    {
      label: i18n.t('form.label.address'),
      value: store.personalDetails?.mailingAddress ? transformDashboardAddress(store.personalDetails?.mailingAddress) : '',
    },
    {
      label: i18n.t('form.label.estimated_annual_turnover'),
      value: store.personalDetails?.annualTurnover
        ? i18nGlobal.global.t(`entity.AnnualTurnover.${store.personalDetails?.annualTurnover}`) : undefined,
    },
    {
      label: i18n.t('form.label.funds_source'),
      value: store.personalDetails?.fundsSource ? i18nGlobal.global.t(`entity.FundsSource.${store.personalDetails?.fundsSource}`) : undefined,
    },
    {
      label: i18n.t('form.label.occupation'),
      value: store.personalDetails?.occupation ?? '',
    },
    {
      label: i18n.t('pages.onboarding.admin_page.account_created'),
      value: store.createAt ? getDate(store.createAt) : '',
    },
  ],
}));
</script>
